import "../utils/renderingUtils";
import { useRef } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Box, Flex } from "@chakra-ui/react";
import { DotLoader } from "@euka-mfes/bark";

import { ChatWidgetWrapper } from "@/components/chat";
import HelpButton from "@/components/HelpButton";
import Header from "@components/Header";

import { useStudentData } from "@/context";
import { useUserSelector } from "@context/hooks";

import { getCurrentSectionFromPath } from "@/utils/lessonUtils";
import { buildLessonCode, usePageTitle } from "@/utils/pageTitle";

import { LearnosityProvider, useLearnosity } from "@shared/learnosity";

import { usePrism } from "@/hooks/usePrism";
import { useMathJax } from "@/hooks/useMathJax";
import { usePlotly } from "@/hooks/usePlotly";

// Bootstrap hook (fetches header details, sets grade)

// Lesson entry rules (completed → summary, etc.)
import useLessonEntryGuard from "@hooks/useLessonEntryGuard";

// Lesson-specific hooks
import useLessonRouteReady from "@hooks/lessons/useLessonRouteReady";
import useLessonSectionContent from "@hooks/lessons/useLessonSectionContent";
import useLearnosityTableWrap from "@hooks/lessons/useLearnosityTableWrap";
import useSuppressLearnosityRejections from "@hooks/lessons/useSuppressLearnosityRejections";

/**
 * Builds the document title for the lesson page.
 */
const buildLessonTitle = (
  gradeName?: string,
  lessonCode?: string,
  subjectName?: string,
  termId?: string,
  weekId?: string,
  lessonNumber?: number
): string => {
  if (lessonCode && subjectName) return `${lessonCode} - ${subjectName}`;
  if (gradeName && termId && weekId && lessonNumber) {
    return `${gradeName} Term ${termId} Week ${weekId} Lesson ${lessonNumber}`;
  }
  return gradeName ?? "Lesson";
};

// BLD2-609: kept as a separate component so the content-renderer hooks
// (usePrism / useMathJax / usePlotly) are only invoked once the parent has
// passed its loader gate and is actually rendering the Box. If these hooks
// ran during the loader phase, contentRef.current would be null when their
// effects first fired; they would bail out, and would never re-run when
// the ref later attached (effect deps don't observe ref mutations).
const LessonContentFrame = ({
  isLearnosityReady
}: {
  isLearnosityReady: boolean;
}) => {
  const contentRef = useRef<HTMLDivElement | null>(null);

  // Content renderers
  usePrism(contentRef, isLearnosityReady);
  useMathJax(contentRef, isLearnosityReady);
  usePlotly(contentRef, isLearnosityReady);

  return (
    <Box
      flex="1"
      w="full"
      pt="var(--euka-header-height, 96px)"
      ref={contentRef}
    >
      <Outlet />
    </Box>
  );
};

const LessonsLayoutContent = () => {
  const { pathname } = useLocation();
  const currentSection = getCurrentSectionFromPath(pathname);

  // Bootstrap: fetch header details and set grade

  // Lesson entry rules (redirect completed lessons to summary, etc.)
  useLessonEntryGuard();

  // Check if data is ready
  const isRouteReady = useLessonRouteReady();

  // Get data from context (derived from URL)
  const { selectedTerm, selectedWeek, selectedLesson, selectedSubject } = useStudentData();
  const { selectedGrade } = useUserSelector();
  const { isLearnosityReady } = useLearnosity();

  const grade = selectedGrade?.grade_code;
  const termId = selectedTerm?.id;
  const weekId = selectedWeek?.id;
  const lessonId = selectedLesson?.id;
  const subjectSlug = selectedSubject?.slug;

  // Fetch section content + manage Learnosity init + prefetch
  useLessonSectionContent({
    currentSection,
    grade,
    termId,
    weekId,
    lessonId,
    subjectSlug
  });

  // Wrap Learnosity tables once ready
  useLearnosityTableWrap(isLearnosityReady);

  // Suppress known Learnosity SDK unhandled rejections
  useSuppressLearnosityRejections();

  // Document title
  const lessonCode = buildLessonCode(
    selectedGrade?.grade_code,
    selectedTerm?.id,
    selectedWeek?.id,
    selectedLesson?.id
  );
  usePageTitle(
    buildLessonTitle(
      selectedGrade?.name?.trim(),
      lessonCode,
      selectedSubject?.name,
      selectedTerm?.id,
      selectedWeek?.id,
      selectedLesson?.id
    )
  );

  // Show loader until data is ready
  if (!isRouteReady) {
    return (
      <Flex w="full" minH="100vh" align="center" justify="center" bg="canvas">
        <DotLoader isLoaded={false} size="lg" />
      </Flex>
    );
  }

  return (
    <>
      <Flex
        flexDir="column"
        w="full"
        minH="100vh"
        bg="canvas"
        position="relative"
      >
        <Header showBreadcrumb />
        <LessonContentFrame isLearnosityReady={isLearnosityReady} />
      </Flex>

      <ChatWidgetWrapper />
      <HelpButton />
    </>
  );
};

const LessonsLayout = () => (
  <LearnosityProvider>
    <LessonsLayoutContent />
  </LearnosityProvider>
);

export default LessonsLayout;
